import { fetchWithToken } from '@vizit/auth';
import { API_BASE_URL } from '@vizit/constants';

/**
 * A failed multipart upload, carrying the API's structured error alongside the message.
 *
 * `message` stays the flattened `Upload failed: <status> <statusText> - <body>` string so existing
 * callers keep their behaviour; `status`/`errorCode`/`detail` are additive so callers that know the
 * endpoint's `error_code`s can map them to their own copy instead of showing server text.
 */
class MultipartUploadError extends Error {
  /** HTTP status of the failed response. */
  status: number;
  /** `error_code` from the API error body, or `null` when the body has none (or isn't JSON). */
  errorCode: string | null;
  /** `detail` from the API error body — server text, for logging or parsing, never for rendering. */
  detail: string | null;

  constructor(input: { message: string; status: number; errorCode: string | null; detail: string | null }) {
    super(input.message);

    this.name = 'MultipartUploadError';
    Object.setPrototypeOf(this, MultipartUploadError.prototype);

    this.status = input.status;
    this.errorCode = input.errorCode;
    this.detail = input.detail;
  }
}

/** Read `error_code`/`detail` out of the standard API error envelope; anything else yields nulls. */
function parseErrorBody(body: string): { errorCode: string | null; detail: string | null } {
  try {
    const parsed: unknown = JSON.parse(body);

    if (parsed && typeof parsed === 'object') {
      const { error_code: errorCode, detail } = parsed as { error_code?: unknown; detail?: unknown };

      return {
        errorCode: typeof errorCode === 'string' ? errorCode : null,
        detail: typeof detail === 'string' ? detail : null,
      };
    }
  } catch {
    // Not JSON (proxy HTML, plain text). The raw body is still on `message` for logging.
  }

  return { errorCode: null, detail: null };
}

/**
 * Performs a multipart/form-data POST request with authentication
 *
 * @param path - The API path (without base URL)
 * @param file - The file or blob to upload
 * @param pathParams - Path parameters to replace in the URL
 * @param formFields - Extra form fields sent alongside `file`. An array value is appended once
 * per item under the same key (how FastAPI reads a repeated `Form()` list); null/undefined
 * values are omitted entirely, so optional fields cost nothing when absent.
 * @returns The response data
 * @throws {MultipartUploadError} When the response is not ok.
 */
export async function multipartPost<T>(
  path: string,
  file: Blob | File,
  pathParams?: Record<string, string>,
  formFields?: Record<string, string | string[] | null | undefined>,
): Promise<T> {
  let url = `${API_BASE_URL}${path}`;

  // Replace path parameters
  if (pathParams) {
    for (const [key, value] of Object.entries(pathParams)) {
      url = url.replace(`{${key}}`, encodeURIComponent(value));
    }
  }

  const formData = new FormData();
  formData.append('file', file);

  for (const [key, value] of Object.entries(formFields ?? {})) {
    if (value == null) {
      continue;
    }
    for (const item of Array.isArray(value) ? value : [value]) {
      formData.append(key, item);
    }
  }

  const response = await fetchWithToken(url, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new MultipartUploadError({
      message: `Upload failed: ${response.status} ${response.statusText} - ${errorText}`,
      status: response.status,
      ...parseErrorBody(errorText),
    });
  }

  return response.json();
}

export { MultipartUploadError };
